import React from 'react';
import { toast as sonnerToast } from 'sonner';

/**
 * Safely normalizes message content to ensure undefined, null, or Error instances
 * don't cause render anomalies.
 */
function normalizeMessage(message, defaultFallback = 'An error occurred') {
  if (message === null || message === undefined || message === '') {
    return defaultFallback;
  }
  if (message instanceof Error) {
    return message.message || defaultFallback;
  }
  if (typeof message === 'object' && !React.isValidElement(message)) {
    if (typeof message.message === 'string') {
      return message.message;
    }
    try {
      return JSON.stringify(message);
    } catch {
      return defaultFallback;
    }
  }
  return message;
}

/**
 * Normalizes legacy react-toastify options to native Sonner options.
 * Maps autoClose -> duration, toastId -> id, and onClose -> onDismiss/onAutoClose.
 * Strips obsolete react-toastify flags so they don't pollute Sonner toast state.
 */
function normalizeOptions(options = {}) {
  if (!options || typeof options !== 'object') return options;

  const normalized = { ...options };

  // Map autoClose -> duration
  if ('autoClose' in normalized) {
    if (normalized.autoClose === false) {
      normalized.duration = Infinity;
    } else if (typeof normalized.autoClose === 'number') {
      normalized.duration = normalized.autoClose;
    }
    delete normalized.autoClose;
  }

  // Map toastId -> id (for deduplication)
  if ('toastId' in normalized) {
    normalized.id = normalized.toastId;
    delete normalized.toastId;
  }

  // Map onClose callback
  if (typeof normalized.onClose === 'function') {
    const originalOnClose = normalized.onClose;
    normalized.onDismiss = (t) => originalOnClose(t);
    normalized.onAutoClose = (t) => originalOnClose(t);
    delete normalized.onClose;
  }

  // Clean up react-toastify specific keys
  delete normalized.hideProgressBar;
  delete normalized.closeOnClick;
  delete normalized.pauseOnHover;
  delete normalized.draggable;
  delete normalized.progress;
  delete normalized.theme;
  delete normalized.transition;
  delete normalized.type;

  return normalized;
}

export const toast = Object.assign(
  (message, opts) => sonnerToast(normalizeMessage(message, 'Notice'), normalizeOptions(opts)),
  {
    success: (message, opts) => sonnerToast.success(normalizeMessage(message, 'Success'), normalizeOptions(opts)),
    error: (message, opts) => sonnerToast.error(normalizeMessage(message, 'An error occurred'), normalizeOptions(opts)),
    info: (message, opts) => sonnerToast.info(normalizeMessage(message, 'Information'), normalizeOptions(opts)),
    warning: (message, opts) => sonnerToast.warning(normalizeMessage(message, 'Warning'), normalizeOptions(opts)),
    warn: (message, opts) => sonnerToast.warning(normalizeMessage(message, 'Warning'), normalizeOptions(opts)),
    loading: (message, opts) => sonnerToast.loading(normalizeMessage(message, 'Loading...'), normalizeOptions(opts)),
    dismiss: (id) => sonnerToast.dismiss(id),
    promise: (promise, data) => sonnerToast.promise(promise, data),
    custom: (jsx, opts) => sonnerToast.custom(jsx, normalizeOptions(opts)),
    message: (message, opts) => sonnerToast.message(normalizeMessage(message, 'Notice'), normalizeOptions(opts)),
    isActive: () => false,
  }
);

export default toast;
